import type { MapCell, HeatScale } from "@/lib/api/map";
import { cellState, CELL_STATE_LABEL } from "@/lib/api/map";

/**
 * Xaritadagi RANG: uch xil savolga uch xil javob.
 *
 * Bitta rang bitta savolga javob beradi va ularni aralashtirib
 * bo'lmaydi:
 *
 *   - «holat» — katakda joy bormi, bloklanganmi. Kundalik ish.
 *   - «ABC» — katakda qanday toifadagi tovar turibdi va u o'z
 *     hududidami. Slotting qarori.
 *   - «harakat» — katakka qanchalik tez-tez tegiladi. Issiq
 *     nuqtalar: marshrutni aynan shular uzaytiradi.
 *
 * Hammasini bitta chizmada ko'rsatishga urinish rangni
 * o'qilmaydigan holga keltirardi — shuning uchun KO'RINISH
 * almashadi, ranglar qo'shilmaydi.
 *
 * Rang kaliti 2D va 3D uchun BIR XIL: ikki ko'rinish bir xil
 * katakni boshqacha rang bilan ko'rsatsa, xodim qaysi biriga
 * ishonishni bilmasdi.
 */
export type ColorMode = "state" | "abc" | "heat";

export const COLOR_MODE_LABEL: Record<ColorMode, string> = {
  state: "Holat",
  abc: "ABC",
  heat: "Harakat",
};

export interface Tone {
  /** CSS klassi va 3D rangining umumiy kaliti. */
  key: string;
  /** Katak ustidagi izohga tushadigan matn. */
  label: string;
  /**
   * Tovar o'z ABC hududida EMAS.
   *
   * Rang bilan emas, alohida belgi bilan ko'rsatiladi: bu
   * toifaning o'zi emas, undagi XATO va u toifadan ustun turadi.
   */
  mismatch: boolean;
}

/** Harakat ro'yxati — katak IDsi bo'yicha. */
export type HeatIndex = Map<string, { moves: number; picks: number }>;

const ABC_LABEL: Record<string, string> = {
  A: "A toifa — tez aylanadi",
  B: "B toifa — o'rtacha",
  C: "C toifa — sust",
};

const ZONE_LABEL: Record<string, string> = {
  A: "A hududi — bo'sh",
  B: "B hududi — bo'sh",
  C: "C hududi — bo'sh",
};

export function cellTone(
  cell: MapCell,
  mode: ColorMode,
  heat?: { index: HeatIndex; scale: HeatScale },
): Tone {
  if (mode === "abc") return abcTone(cell);
  if (mode === "heat") return heatTone(cell, heat);

  const state = cellState(cell);
  return { key: `state-${state}`, label: CELL_STATE_LABEL[state], mismatch: false };
}

/**
 * ABC ko'rinishi: rang — katakda TURGAN tovarning toifasi.
 *
 * Bo'sh katak o'zining LOYIHADAGI hududini xira ko'rsatadi:
 * shunda ABC zonalashtirishning o'zi ham chizmada ko'rinadi va
 * «A hududida qancha bo'sh joy bor» degan savolga javob bo'ladi.
 */
function abcTone(cell: MapCell): Tone {
  const zone = (cell.abc_zone ?? "").toUpperCase();
  const goods = (cell.sku_abc_class ?? "").toUpperCase();

  if (!cell.is_active) {
    return { key: "state-inactive", label: CELL_STATE_LABEL.inactive, mismatch: false };
  }

  if (!goods) {
    return {
      key: zone ? `zone-${zone.toLowerCase()}` : "zone-none",
      label: ZONE_LABEL[zone] ?? "Hudud belgilanmagan",
      mismatch: false,
    };
  }

  // Hududsiz katakda (bufer, bojxona) «mos kelmadi» degan
  // tushuncha yo'q: solishtiradigan narsa yo'q.
  const mismatch = zone !== "" && zone !== goods;
  return {
    key: `abc-${goods.toLowerCase()}`,
    label: mismatch
      ? `${ABC_LABEL[goods] ?? goods} · ${zone} hududida turibdi`
      : (ABC_LABEL[goods] ?? goods),
    mismatch,
  };
}

/**
 * Harakat ko'rinishi: rang — oynadagi tegishlar soni.
 *
 * Chegaralar BUTUN KORPUS shkalasidan keladi (serverdan), ya'ni
 * bir qatordagi qizil boshqa qatordagi qizil bilan bir xil
 * ma'noni bildiradi. Shkala ko'rinayotgan kataklardan qurilsa,
 * har bir qatorning eng band katagi «eng issiq» bo'lib qizarardi.
 */
function heatTone(cell: MapCell, heat?: { index: HeatIndex; scale: HeatScale }): Tone {
  if (!cell.is_active) {
    return { key: "state-inactive", label: CELL_STATE_LABEL.inactive, mismatch: false };
  }

  const point = heat?.index.get(cell.location_id);
  const moves = point?.moves ?? 0;
  const level = heatLevel(moves, heat?.scale);
  if (level === 0) {
    return { key: "heat-0", label: "Harakat yo'q", mismatch: false };
  }
  const picks = point?.picks ?? 0;
  return {
    key: `heat-${level}`,
    label: `${moves} harakat${picks > 0 ? `, shundan ${picks} terish` : ""}`,
    mismatch: false,
  };
}

export function heatLevel(moves: number, scale?: HeatScale): 0 | 1 | 2 | 3 {
  if (moves <= 0) return 0;
  // Shkala hali bo'sh (harakat endi boshlangan): hammasi bir xil
  // «iliq». Yolg'on darajalash ko'rsatishdan ko'ra halolroq.
  if (!scale || scale.p90 <= 0) return 1;
  if (moves > scale.p90) return 3;
  if (moves > scale.median) return 2;
  return 1;
}

/**
 * Harakat legendasining bandlari — AYNAN SONLAR bilan.
 *
 * «Kam / o'rtacha / ko'p» o'zicha hech nima demaydi: xodim
 * rangning ortida qanday son turganini bilishi kerak. Mediana
 * bilan p90 ustma-ust tushsa (harakat juda kam) band tashlab
 * yuboriladi — «4…3» degan oraliq ma'nosiz.
 */
export function heatBands(scale: HeatScale): { key: string; label: string }[] {
  const bands: { key: string; label: string }[] = [
    { key: "heat-0", label: "Harakatsiz" },
  ];
  if (scale.max <= 0) return bands;

  const low = Math.max(scale.median, 1);
  bands.push({ key: "heat-1", label: low === 1 ? "1 harakat" : `1–${low}` });
  if (scale.p90 > low) bands.push({ key: "heat-2", label: `${low + 1}–${scale.p90}` });
  if (scale.max > scale.p90) {
    bands.push({ key: "heat-3", label: `${scale.p90 + 1} dan ortiq` });
  }
  return bands;
}

/**
 * 3D ko'rinish uchun ranglar — CSS bilan BIR XIL qiymatlar.
 *
 * Takrorlanishi ataylab: `three.js` CSS o'zgaruvchilarini
 * o'qiy olmaydi. Qiymat o'zgarsa ikkala joyda o'zgartiriladi va
 * aynan shuning uchun ular yonma-yon turadi (`map.css` dagi
 * «Xarita ranglari» bo'limiga qarang).
 */
export const TONE_COLOR: Record<string, number> = {
  "state-empty": 0xdfe4ec,
  "state-partial": 0x4a9dff,
  "state-full": 0x1f6feb,
  "state-blocked": 0xe5484d,
  "state-inactive": 0x9aa1ab,
  "state-no-putaway": 0xf5a524,

  "abc-a": 0xf97316,
  "abc-b": 0xfbbf24,
  "abc-c": 0x60a5fa,
  "zone-a": 0xfbe3cf,
  "zone-b": 0xfaf0d2,
  "zone-c": 0xdfeaf9,
  "zone-none": 0xe8ecf2,

  "heat-0": 0xe8ecf2,
  "heat-1": 0xffe08a,
  "heat-2": 0xff9f43,
  "heat-3": 0xe5484d,
};

export function toneColor(key: string): number {
  return TONE_COLOR[key] ?? 0xdfe4ec;
}
